// Dev/QA plan fixtures — a governance-plane view-model per scope so the plan drawer renders at rest.
// Backend-required in production (/api/v1/plan/*); these are the consumer-expectation shapes.

import type { PlanScope } from './plan-types';

const TAS: PlanScope = {
  scopeId: 'p_tas',
  kindLabel: 'PROJECT',
  projectName: 'TAS owner-builder',
  postureLine: '4 events · 1 needs review · grounded on verified-facts memory',
  ownerInitial: 'M',
  ownerName: 'You',
  ownerColor: 'var(--accent)',
  canEdit: true,
  goals: [
    {
      id: 'g_tas_1',
      text: 'Ship a reusable owner-builder intake brief, TAS-first',
      provenance: 'human',
      statusLabel: '2 approved · 1 running',
      statusColor: 'var(--green-fg)',
      lifecycle: 'active',
      linkedEventCount: 4,
      ownerInitial: 'M',
      ownerName: 'You',
      ownerColor: 'var(--accent)',
    },
    {
      id: 'g_tas_2',
      text: 'Confirm the CBOS permit pathway before reuse',
      provenance: 'ai-approved',
      statusLabel: '1 needs review',
      statusColor: 'var(--amber)',
      lifecycle: 'active',
      linkedEventCount: 1,
    },
  ],
  milestones: [
    { id: 'm_tas_1', text: 'Eligibility section drafted', provenance: 'human', linkedEventCount: 1 },
    { id: 'm_tas_2', text: 'Permit pathway confirmed', isNext: true, provenance: 'ai-pending', linkedEventCount: 2 },
    { id: 'm_tas_3', text: 'Brief promoted to reusable model', provenance: 'derived' },
  ],
  todos: [
    { id: 't_tas_1', text: 'Capture QBCC insurance minimums', provenance: 'ai-pending', canPromote: true },
    { id: 't_tas_2', text: 'Cross-link the fees table to the CBOS source', provenance: 'human', canPromote: true },
  ],
  intents: [
    { id: 'i_tas_1', label: 'Draft the reusable intake brief', lifecycle: 'active', settled: false, progressPct: 65 },
    { id: 'i_tas_2', label: 'Verify QBCC references', lifecycle: 'completed', settled: true },
  ],
  proposals: [
    {
      id: 'pr_tas_1',
      verbLabel: 'Add goal',
      risk: 'low',
      text: 'Add a goal to track the NSW variant once the TAS brief is confirmed as the model.',
      proposedBy: 'Readiness agent',
      affects: 'Goals (new)',
      evidenceLabel: 'NSW Fair Trading pathway note',
      approveVerb: 'Approve & add',
    },
  ],
};

const SUPPLIER: PlanScope = {
  scopeId: 'p_supplier',
  kindLabel: 'PROJECT',
  projectName: 'Supplier onboarding',
  postureLine: '2 events · ABN validation running',
  ownerInitial: 'M',
  ownerName: 'You',
  ownerColor: 'var(--accent)',
  canEdit: true,
  goals: [
    { id: 'g_sup_1', text: 'Standard supplier intake + ABN validation pack', provenance: 'human', lifecycle: 'active', linkedEventCount: 2 },
  ],
  milestones: [{ id: 'm_sup_1', text: 'Intake form scaffolded', isNext: true, provenance: 'human', linkedEventCount: 1 }],
  todos: [{ id: 't_sup_1', text: 'Source the insurance-expiry field', provenance: 'ai-pending', canPromote: true }],
  intents: [],
  proposals: [],
};

const EMPTY = (id: string, name: string): PlanScope => ({
  scopeId: id,
  kindLabel: 'PROJECT',
  projectName: name,
  postureLine: 'No plan content yet — add a goal to begin.',
  canEdit: true,
  goals: [],
  milestones: [],
  todos: [],
  intents: [],
  proposals: [],
});

const BY_SCOPE: Record<string, PlanScope> = { p_tas: TAS, p_supplier: SUPPLIER };

/** The fixture plan view-model for a scope; empty scopes still render section headers + add affordances. */
export function fixturePlan(scopeId: string): PlanScope {
  return BY_SCOPE[scopeId] ?? EMPTY(scopeId, scopeId === 'p_qld' ? 'QLD variant' : scopeId === 'p_nsw' ? 'NSW exploration' : 'Project');
}
